import { Link as MuiLink, TableCell, type TableCellProps } from '@mui/material';
import { ReactElement, useContext } from 'react';
import { NumberField, RecordContextProvider } from 'react-admin';
import { Link as RouterLink } from 'react-router-dom';
import type { MerchantsOfferSummariesResource } from '../../../../../Entities/MerchantsOfferSummary';
import { MetricHoverContext } from './context';
import {
  getOffersSearch,
  getMetricBackground,
  getMetricHoverBackground,
  type MetricKey,
} from './utils';

interface MetricCellProps extends TableCellProps {
  record: MerchantsOfferSummariesResource;
  source: keyof NonNullable<MerchantsOfferSummariesResource['attributes']>;
  metricKey: MetricKey;
  interactive?: boolean;
  highlightOnHover?: boolean;
}

export default function MetricCell({
  record,
  source,
  metricKey,
  interactive = true,
  highlightOnHover = true,
  sx,
  ...props
}: MetricCellProps): ReactElement {
  const { hoveredMetricKey, setHoveredMetricKey } = useContext(MetricHoverContext);
  const active = highlightOnHover && hoveredMetricKey === metricKey;
  const offersUrl = `/offers${getOffersSearch(metricKey, record)}`;

  return (
    <TableCell
      {...props}
      onMouseEnter={highlightOnHover ? () => setHoveredMetricKey(metricKey) : undefined}
      onMouseLeave={highlightOnHover ? () => setHoveredMetricKey(null) : undefined}
      sx={[
        {
          position: 'relative',
          minWidth: 88,
          height: 32,
          textAlign: 'center',
          whiteSpace: 'nowrap',
          cursor: interactive ? 'pointer' : undefined,
          bgcolor: active ? getMetricHoverBackground(metricKey) : getMetricBackground(metricKey),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <RecordContextProvider value={record}>
        {interactive ? (
          <MuiLink
            component={RouterLink}
            to={offersUrl}
            color="inherit"
            underline="none"
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <NumberField source={`attributes.${source}`} options={{ maximumFractionDigits: 0 }} />
          </MuiLink>
        ) : (
          <NumberField source={`attributes.${source}`} options={{ maximumFractionDigits: 0 }} />
        )}
      </RecordContextProvider>
    </TableCell>
  );
}
